import React from 'react';

import { colors } from '@/globalStyles';

import { detectAllFonts } from '../../../helpers/general';
import BasePrintRenderer from './BasePrintRenderer';
import LineComponent from '../../shared/PrimitiveRenderer/Line';
import CircleComponent from '../../shared/PrimitiveRenderer/Circle';
import PolygonComponent from '../../shared/PrimitiveRenderer/Polygon';
import RectComponent from '../../shared/PrimitiveRenderer/Rect';
import ArcComponent from '../../shared/PrimitiveRenderer/Arc';
import TextComponent from '../../shared/PrimitiveRenderer/Text';

const previewOffset = 1; // mm

export default class PagePrintRenderer extends BasePrintRenderer {
  getAllLayers() {
    const { printObjId: pageUid, pages } = this.props;
    return { page: pages[pageUid] };
  }

  buildRenderedShapes(page) {
    const { decorations, solid, holes } = page;

    let renderShapes = [];

    if (this.isPreviewPrint) {
      renderShapes = renderShapes.concat(solid).concat(decorations);
    }

    const shieldsToRender = super.buildRenderedShapes(page);

    renderShapes = renderShapes.concat(shieldsToRender);

    if (this.isPreviewPrint) {
      renderShapes = renderShapes.concat(holes);
    }

    return renderShapes;
  }

  render() {
    const {
      transforms,
      materialType,
      materialPrintParameters: {
        printing_offsets: { b: offB, l: offL, r: offR, t: offT },
      },
    } = this.props;

    this.checkBaseProps();

    const { page: currentPage } = this.getAllLayers();
    const {
      sections,
      box: { r, b, l, t },
    } = currentPage;
    
    const renderShapes = this.buildRenderedShapes(currentPage);
    const { decorations } = currentPage;
    const pageRectStrokeStyle = { stroke: 'none' };
    // all calculations in MM units
    const widthOffset = this.isPreviewPrint ? previewOffset * 2 : offR - offL;
    const width = r + widthOffset;
    const heightOffset = this.isPreviewPrint ? previewOffset * 2 : offB - offT;
    const height = b + heightOffset;
    const offsetX = this.isPreviewPrint ? -previewOffset : offL;
    const offsetY = this.isPreviewPrint ? -previewOffset : offT;
    const svgRect = { x: offsetX, y: offsetY, width, height };
    const otherOptions = { fonts: detectAllFonts(sections) };
    const borderLines = this.buildBorderLines({ currentPage, renderShapes });

    return this.renderSvg(
      svgRect,
      !this.isPreviewPrint && transforms,
      otherOptions,
      <>
        {renderShapes.map(shape => this.mapShape(shape, currentPage))}
        {this.isPreviewPrint && (
          <rect
            x={l}
            y={t}
            width={r}
            height={b}
            fill={colors.white}
            fillOpacity="0"
            {...pageRectStrokeStyle}
          />
        )}
        {Object.values(sections).map(section => this.mapSection(section, currentPage.resizeType))}
        {decorations.map(deco => {
          if (deco.primitiveType === 'line' && deco.printable) {
            return <LineComponent
                key={`deco_line_${deco.id}`}
                primitiveProps={deco.primitiveProps}
                style={deco.primitiveProps.style? deco.primitiveProps.style : {}}
              />
          } else if (deco.primitiveType === 'circle' && deco.printable) {
            return <CircleComponent
                key={`deco_circle_${deco.id}`}
                primitiveProps={deco.primitiveProps}
                style={deco.primitiveProps.style? deco.primitiveProps.style : {}}
                />
          } else if (deco.primitiveType === 'rect' && deco.printable) {
            return <RectComponent
                key={`deco_rect_${deco.id}`}
                primitiveProps={deco.primitiveProps}
                style={deco.primitiveProps.style? deco.primitiveProps.style : {}}
                />
          } else if (deco.primitiveType === 'arc' && deco.printable) {
            return <ArcComponent
                key={`deco_arc_${deco.id}`}
                primitiveProps={deco.primitiveProps}
                style={deco.primitiveProps.style? deco.primitiveProps.style : {}}
                />
          } else if (deco.primitiveType === 'polygon' && deco.printable) {
            return <PolygonComponent
                key={`deco_polygon_${deco.id}`}
                primitiveProps={deco.primitiveProps}
                style={deco.primitiveProps.style? deco.primitiveProps.style : {}}
                />
          } else if (deco.primitiveType === 'text' && deco.printable) {
            return <TextComponent
                key={`deco_text_${deco.id}`}
                primitiveProps={deco.primitiveProps}
                style={deco.primitiveProps.style? deco.primitiveProps.style : {}}
                />
          } else {
            return ''
          }
        })}
        {borderLines.map(({ position, section, primitiveProps, style }) => {
          return <LineComponent key={`line_${position}_${section.id}`} style={style} primitiveProps={primitiveProps} />;
        })}
      </>,
    );
  }
}
